import React from 'react';
import classNames from 'classnames';
import Password from '../forms/input/password';
import { Form } from '../forms';
import { Item } from '../forms/form/item';
import {
    Check,
    Circle,
    CloseLarge,
    Help
} from '@nackle/origami-icons';
import { languageChangeEvent } from '@nackle/intl-tools';
import { NackleCaption } from '../../exports';
import { getString } from '@nackle/intl-tools';

/**
 * A password requirements helper component
 * @module NacklePasswordHelper
 * @param {Object} props - the password helper props
 * @param {string} [props.value] - the current password value
 * @param {string} [props.confirmPasswordValue] - the current value of the confirmPassword field
 * @param {string} [props.name] - the name of the input
 * @param {string} [props.label] - the label of the input
 * @param {React.ReactElement} [props.helpComponent] - the help component for the input
 * @param {boolean} [props.localeUpdated] - whether or not the password helper locale updated
 * @param {string} [props.instructions] - the instructions for the input
 * @param {string} [props.allInstructions] - all instructions for the input
 * @param {string} [props.type] - the type of input
 * @param {string} [props.customPasswordPlaceholder] - an optional custom password input placeholder
 * @param {string} [props.customConfirmPasswordPlaceholder] - an optional custom confirm password input placeholder
 * @param {string} [props.size] - the size of the input
 * @param {Object} [props.helperLabelProps] - props for the helper label
 * @param {string} [props.caption] - caption for the input
 * @param {string} [props.tooltipText] - the text for the tooltip
 * @param {Object} [props.helperCaptionProps] - the props for the helper caption
 * @param {boolean} [props.meetsAllCriteria] - callback that fires on input change returning whether all criteria met
 * @param {Object} [props.passwordRules] - the rules for the password
 * @param {Object} [props.passwordRulesLabels] - the labels for the password rules
 * @param {Function} [props.onChangePassword] - the method that calls when password input changes.
 * @param {Function} [props.onChangeConfirmPassword] - the method that calls when password input changes.
 * @param {boolean} [props.displayRequirement] - whether or not to display the password requirements
 * @returns {React.ReactElement} - A password requirements helper component
**/
class NacklePasswordHelper extends React.Component {
    constructor( props ) {
        super( props );
        this.pwordRef = React.createRef();
        this.confirmPwordRef = React.createRef();

        this.state = {
            focused: false,
            focusedConfirm: false,
            allCriteriaMet: false,
            complexityCriteriaMet: false,
            validation: {},
            localeUpdated: false,
            confirmPassword: '',
            passwordMatch: '',
            showError: false
        };
    }

    static getDerivedStateFromProps( props, state ) {

        const {
            value = '',
            meetsAllCriteria,
            passwordRules,
            passwordRules: { complexityRequirement }
        } = props;

        const {
            allCriteriaMet: prevCriteriaMet,
            currentPwdLength: prevPasswordLength
        } = state;

        const complexity = passwordRules?.complexity || [];
        const currentPwdLength = value.length;

        if ( prevCriteriaMet && prevPasswordLength === currentPwdLength ) {
            return null;
        }

        const complexityTests = {
            isLowerCaseRequired: /^(?=.*[a-z]).+$/,
            isUpperCaseRequired: /^(?=.*[A-Z]).+$/,
            isSpecialCharRequired: /[-!$%#@^&*()_+|~=`{}[\]:";'<>?,./\\]/,
            isNumberRequired: /[0-9]/
        };

        const validation = {
            lengthValid: currentPwdLength >= passwordRules?.minLength,
            complexityValidation: {}
        };

        let complexityResults = [];
        let complexityCount = 0;
        let complexityCriteriaMet = false;
        let allCriteriaMet = false;

        complexity.forEach( rule => {
            validation.complexityValidation[ rule ] = complexityTests[ rule ].test( value );
        } );

        complexityResults = Object.values( validation.complexityValidation );

        complexityCount = complexityResults.reduce( ( count, valid ) => {
            return valid ? ++count : count;
        }, 0 );

        complexityCriteriaMet = complexityCount >= complexityRequirement;

        allCriteriaMet = validation.lengthValid && complexityCriteriaMet;

        if ( typeof meetsAllCriteria === 'function' ) {
            meetsAllCriteria( allCriteriaMet );
        }
        return {
            validation,
            complexityCriteriaMet,
            allCriteriaMet,
            currentPwdLength
        };
    }

    handleFocus = ( event, type ) => {
        const { onFocus } = this.props;
        event && event.persist();
        if ( type === 'confirm_password' ) {
            this.setState( { focusedConfirm: true } );
            this.setState( { focused: true } );
            this.setState( { showError: true } );
        } else {
            this.setState( { focused: true } );
        }
        if ( typeof onFocus === 'function' ) {
            onFocus( event );
        }
    };

    handleBlur = ( event ) => {
        const { onBlur } = this.props;
        event && event.persist();
        const { displayRequirement } = this.props;
        if ( !displayRequirement ) {
            this.setState( { focusedConfirm: false } );
            this.setState( { focused: false } );
        }
        if ( typeof onBlur === 'function' ) {
            onBlur( event );
        }
    };
    checkPasswordsMatch = () => {
        const { confirmPasswordValue, value } = this.props;
        if ( confirmPasswordValue && confirmPasswordValue.length != 0 ) {
            this.setState( { passwordMatch: confirmPasswordValue === value } );
        } else {
            this.setState( { passwordMatch: '' } );
        }
    };
    onChangePassword = ( e ) => {
        this.props.onChangePassword( e );
        this.setState( { showError: false } );

    };
    onChangeConfirmPassword = ( e ) => {
        const { validation: { lengthValid }, complexityCriteriaMet } = this.state;
        const passwordMatch = e.target.value === this.props.value;
        const validPassword = ( passwordMatch && lengthValid && complexityCriteriaMet );

        this.setState( { isPasswordValid: validPassword }, this.props.onChangeConfirmPassword( e.target.value, validPassword ) );
    };

    componentDidMount() {
        languageChangeEvent.on( ( { localeCode, bundle } ) => {
            this.setState( {
                localeUpdated: !this.state.localeUpdated
            } );
        } );
        this.checkPasswordsMatch();
    }

    componentDidUpdate( prevProps, prevState ) {
        if ( this.props.confirmPasswordValue !== prevProps.confirmPasswordValue ||
            this.props.value !== prevProps.value ) {
            this.checkPasswordsMatch();
        }
        if ( this.props.displayRequirement !== prevProps.displayRequirement ) {
            this.handleBlur();
        }
    }
    componentWillUnmount() {
        clearTimeout( null );
    }

    render() {
        const {
            className,
            passwordRules = {},
            passwordRulesLabels = {},
            value,
            name,
            label,
            helpComponent,
            meetsAllCriteria,
            localeUpdated,
            instructions,
            allInstructions,
            type,
            customPasswordPlaceholder,
            customConfirmPasswordPlaceholder,
            confirmPasswordValue,
            size,
            helperLabelProps,
            caption,
            tooltipText,
            helperCaptionProps,
            displayRequirement,
            ...props
        } = this.props;

        const complexityRules = passwordRules?.complexity || [];
        const complexityLabels = passwordRulesLabels?.complexity || {};
        const {
            complexityCriteriaMet,
            validation: { complexityValidation, lengthValid },
            focusedConfirm,
            showError,
            passwordMatch,
            allCriteriaMet
        } = this.state;
        const classes = classNames(
            'nkl-password-helper',
            'nkl-text-input',
            className
        );

        const minLengthClass = value === '' ?
            'length nkl-password-helper--initial' : lengthValid
                ? 'length nkl-password-helper--passes'
                : 'length nkl-password-helper--fails';

        const complexityClass = value === '' ?
            'complexity nkl-password-helper--initial' :
            complexityCriteriaMet
                ? 'complexity nkl-password-helper--passes'
                : 'complexity nkl-password-helper--fails';

        /* Default value */
        const labelObj = {
            password: getString( 'origami.passwordHelper.password' ),
            confirmPassword: getString( 'origami.passwordHelper.confirmPass' )
        };
        const captionDefault = getString( 'origami.passwordHelper.criteria.confirmPass' );

        return (
            <div className={ classes } style={ { 'width': props.width } }>

                {
                    helpComponent
                    && <span className={ 'nkl-input-helper' }>{ helpComponent }</span>
                }
                <div
                    className='nkl-password-helper-inner'
                    id={ 'password-input' }
                    ref={ this.pwordRef }>
                    {
                        type === 'CONFIRM_PASSWORD' ?
                            <Form
                                name="dependencies"
                                autoComplete="off"
                                layout="vertical"
                                { ...props }
                            >
                                <Item
                                >
                                    <Password
                                        label={ label === false ? false : true }
                                        name={ name }
                                        size={ size }
                                        value={ value }
                                        labelProps={ {
                                            text: helperLabelProps?.text ? helperLabelProps.text : labelObj.password,
                                            icon: helperLabelProps?.tooltipText ? <Help /> : null,
                                            size: { size },
                                            ...helperLabelProps
                                        } }
                                        caption={ caption === false ? false : true }
                                        onChange={ ( e ) => this.onChangePassword( e ) }
                                        { ...props }
                                        placeholder={ customPasswordPlaceholder }
                                        onBlur={ ( e ) => this.handleBlur( e ) }
                                        onFocus={ ( e ) => this.handleFocus( e, 'password' ) }
                                        captionProps={ {
                                            size: size, text:
                                                
                                                    <div className={ 'nkl-password-helper-rules' } aria-label="password requirements" >
                                                        <ul>
                                                            <li className={ minLengthClass }>
                                                                { value === '' ? <Circle className={ 'nkl-icon' } /> : lengthValid ? <Check className={ 'nkl-icon' } /> : <CloseLarge className={ 'nkl-icon' } /> }

                                                                <div>{ passwordRulesLabels?.minLength }</div>
                                                            </li>

                                                            <li className={ complexityClass }>
                                                                <div className='complex-requirement'>
                                                                    { value === '' ? <Circle className={ 'nkl-icon' } /> : complexityCriteriaMet ? <Check className={ 'nkl-icon' } /> : <CloseLarge className={ 'nkl-icon' } /> }

                                                                    <div>{ passwordRulesLabels?.complexityRequirement }</div>
                                                                </div>
                                                                <ul>
                                                                    { complexityRules.map( rule => {
                                                                        const classes = value === '' ?
                                                                            'nkl-password-helper--initial' :
                                                                            complexityValidation[ rule ]
                                                                                ? 'nkl-password-helper--passes' : showError ? 'nkl-password-helper--initial' : 'nkl-password-helper--initial';
                                                                        return <li key={ rule } className={ classes } >
                                                                            { value === '' ? <Circle className={ 'nkl-icon' } /> : complexityValidation[ rule ] ? <Check className={ 'nkl-icon' } /> : showError ? <Circle className={ 'nkl-icon' } /> : <Circle className={ 'nkl-icon' } /> }

                                                                            <div>{ complexityLabels[ rule ] }</div>
                                                                        </li>;
                                                                    } ) }
                                                                </ul>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                
                                        } }
                                    />
                                </Item>
                                <Item
                                >
                                    <Password
                                        label={ label === false ? false : true }
                                        name={ name }
                                        size={ size }
                                        labelProps={ {
                                            ...helperLabelProps,
                                            text: helperLabelProps?.confirmText ? helperLabelProps.confirmText : labelObj.confirmPassword,
                                            size: { size },
                                        } }
                                        caption={ caption === false ? false : true }
                                        value={ confirmPasswordValue }
                                        onChange={ ( thing ) => this.onChangeConfirmPassword( thing ) }
                                        onBlur={ ( e ) => this.handleBlur( e ) }
                                        onFocus={ ( e ) => this.handleFocus( e, 'confirm_password' ) }
                                        { ...props }
                                        placeholder={ customConfirmPasswordPlaceholder }
                                        captionProps={ {
                                            size: size, text: focusedConfirm || ( !allCriteriaMet && value ) ?
                                                <div className={ passwordMatch === '' ?
                                                    'confirm nkl-password-helper--initial' :
                                                    passwordMatch
                                                        ? 'confirm nkl-password-helper--passes'
                                                        : 'confirm nkl-password-helper--fails' }> { passwordMatch === '' ? <Circle className={ 'nkl-icon' } /> : passwordMatch ? <Check className={ 'nkl-icon' } /> : <CloseLarge className={ 'nkl-icon' } /> } { <NackleCaption size={ size } text={ helperCaptionProps.text || captionDefault } /> } </div>
                                                : null
                                        } }
                                    />
                                </Item>
                            </Form>
                            :
                            <Form>
                                <Item
                                >
                                    <Password
                                        label={ label === false ? false : true }
                                        name={ name }
                                        size={ size }
                                        value={ value }
                                        labelProps={ {
                                            text: helperLabelProps?.text ? helperLabelProps.text : labelObj.password,
                                            icon: helperLabelProps?.tooltipText ? <Help /> : null,
                                            size: { size },
                                            ...helperLabelProps
                                        } }
                                        caption={ caption === false ? false : true }
                                        onChange={ ( e ) => this.onChangePassword( e ) }
                                        placeholder={ customPasswordPlaceholder }
                                        onBlur={ ( e ) => this.handleBlur( e ) }
                                        onFocus={ ( e ) => this.handleFocus( e, 'password' ) }
                                        { ...props }
                                        captionProps={ {
                                            size: size, text:
                                                <div className={ 'nkl-password-helper-rules' } aria-label="password requirements" >
                                                    <ul>
                                                        <li className={ minLengthClass }>
                                                            { value === '' ? <Circle className={ 'nkl-icon' } /> : lengthValid ? <Check className={ 'nkl-icon' } /> : <CloseLarge className={ 'nkl-icon' } /> }

                                                            <div>{ passwordRulesLabels?.minLength }</div>
                                                        </li>

                                                        <li className={ complexityClass }>
                                                            <div className='complex-requirement'>
                                                                { value === '' ? <Circle className={ 'nkl-icon' } /> : complexityCriteriaMet ? <Check className={ 'nkl-icon' } /> : <CloseLarge className={ 'nkl-icon' } /> }

                                                                <div>{ passwordRulesLabels?.complexityRequirement }</div>
                                                            </div>
                                                            <ul>
                                                                { complexityRules.map( rule => {
                                                                    const classes = value === '' ?
                                                                        'nkl-password-helper--initial' :
                                                                        complexityValidation[ rule ]
                                                                            ? 'nkl-password-helper--passes' : showError ? 'nkl-password-helper--initial' : 'nkl-password-helper--initial';
                                                                    return <li key={ rule } className={ classes } >
                                                                        { value === '' ? <Circle className={ 'nkl-icon' } /> : complexityValidation[ rule ] ? <Check className={ 'nkl-icon' } /> : showError ? <Circle className={ 'nkl-icon' } /> : <Circle className={ 'nkl-icon' } /> }

                                                                        <div>{ complexityLabels[ rule ] }</div>
                                                                    </li>;
                                                                } ) }
                                                            </ul>
                                                        </li>
                                                    </ul>
                                                </div>
                                        } }
                                    />
                                </Item>
                            </Form>
                    }

                </div>
            </div>
        );
    }
}

export { NacklePasswordHelper };
